<script setup>
import { reactive, ref, computed, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { useStore } from 'dashboard/composables/store';
import { required, requiredIf } from '@vuelidate/validators';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { REPLY_POLICY } from 'shared/constants/links';
import {
  appendSignature,
  removeSignature,
} from 'dashboard/helper/editorHelper';
import {
  buildContactableInboxesList,
  prepareNewMessagePayload,
  prepareWhatsAppMessagePayload,
} from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper.js';

import ContactSelector from './ContactSelector.vue';
import InboxSelector from './InboxSelector.vue';
import EmailOptions from './EmailOptions.vue';
import MessageEditor from './MessageEditor.vue';
import ActionButtons from './ActionButtons.vue';
import InboxEmptyState from './InboxEmptyState.vue';
import AttachmentPreviews from './AttachmentPreviews.vue';
import Banner from 'dashboard/components/ui/Banner.vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  contacts: { type: Array, default: () => [] },
  contactId: { type: String, default: null },
  selectedContact: { type: Object, default: null },
  targetInbox: { type: Object, default: null },
  currentUser: { type: Object, default: null },
  isCreatingContact: { type: Boolean, default: false },
  isFetchingInboxes: { type: Boolean, default: false },
  isLoading: { type: Boolean, default: false },
  isDirectUploadsEnabled: { type: Boolean, default: false },
  contactConversationsUiFlags: { type: Object, default: null },
  contactsUiFlags: { type: Object, default: null },
  dropdownPosition: { type: Object, default: () => ({}) },

});

const emit = defineEmits([
  'searchContacts',
  'discard',
  'updateSelectedContact',
  'updateTargetInbox',
  'clearSelectedContact',
  'createConversation',
]);

const store = useStore();
const { t } = useI18n();

const showContactsDropdown = ref(false);
const showInboxesDropdown = ref(false);
const showCcEmailsDropdown = ref(false);
const showBccEmailsDropdown = ref(false);
// Controls the WhatsApp policy modal opened from the reply restriction banner.
const showReplyWindowInfoModal = ref(false);
const whatsappMessageTemplates = ref([]);

const isCreating = computed(() => props.contactConversationsUiFlags.isCreating);

const state = reactive({
  message: '',
  subject: '',
  ccEmails: '',
  bccEmails: '',
  attachedFiles: [],
});

const inboxTypes = computed(() => ({
  isEmail: props.targetInbox?.channelType === INBOX_TYPES.EMAIL,
  isTwilio: props.targetInbox?.channelType === INBOX_TYPES.TWILIO,
  isAWhatsAppChannel: props.targetInbox?.channelType === INBOX_TYPES.WHATSAPP,
  isWebWidget: props.targetInbox?.channelType === INBOX_TYPES.WEB,
  isApi: props.targetInbox?.channelType === INBOX_TYPES.API,
  isEmailOrWebWidget:
    props.targetInbox?.channelType === INBOX_TYPES.EMAIL ||
    props.targetInbox?.channelType === INBOX_TYPES.WEB,
  isTwilioSMS:
    props.targetInbox?.channelType === INBOX_TYPES.TWILIO &&
    props.targetInbox?.medium === 'sms',
  isTwilioWhatsApp:
    props.targetInbox?.channelType === INBOX_TYPES.TWILIO &&
    props.targetInbox?.medium === 'whatsapp',
  isWhatsAppGupshupInbox:
    props.targetInbox?.channelType === INBOX_TYPES.API &&
    (props.targetInbox?.settings?.typeApi === 'gupshup' ||
      props.targetInbox?.webhook_url ===
        import.meta.env.VITE_CALLPICKER_API + 'chat/webhook'),
  isMercadoLibreInbox:
    props.targetInbox?.channelType === INBOX_TYPES.API &&
    props.targetInbox?.settings?.typeApi === 'mercadolibre',
}));

const inboxChannelType = computed(() => props.targetInbox?.channelType || '');

const validationRules = computed(() => ({
  selectedContact: { required },
  targetInbox: { required },
  message: {
    required: requiredIf(
      !inboxTypes.value.isAWhatsAppChannel &&
        !inboxTypes.value.isWhatsAppGupshupInbox
    ),
  },
  subject: { required: requiredIf(inboxTypes.value.isEmail) },
}));

const v$ = useVuelidate(validationRules, {
  selectedContact: computed(() => props.selectedContact),
  targetInbox: computed(() => props.targetInbox),
  message: computed(() => state.message),
  subject: computed(() => state.subject),
});

const validationStates = computed(() => ({
  isContactInvalid:
    v$.value.selectedContact.$dirty && v$.value.selectedContact.$invalid,
  isInboxInvalid: v$.value.targetInbox.$dirty && v$.value.targetInbox.$invalid,
  isSubjectInvalid: v$.value.subject.$dirty && v$.value.subject.$invalid,
  isMessageInvalid: v$.value.message.$dirty && v$.value.message.$invalid,
}));

const selectedConversation = computed(() => {
  return props.selectedContact?.conversations?.find(
    conversation => conversation.inbox_id === props.targetInbox?.id
  );
});

const newMessagePayload = () => {
  const { message, subject, ccEmails, bccEmails, attachedFiles } = state;
  return prepareNewMessagePayload({
    targetInbox: props.targetInbox,
    selectedContact: props.selectedContact,
    message,
    subject,
    ccEmails,
    bccEmails,
    currentUser: props.currentUser,
    attachedFiles,
    directUploadsEnabled: props.isDirectUploadsEnabled,
  });
};

const contactableInboxesList = computed(() => {
  return buildContactableInboxesList(props.selectedContact?.contactInboxes);
});

const showNoInboxAlert = computed(() => {
  return (
    props.selectedContact &&
    contactableInboxesList.value.length === 0 &&
    !props.contactsUiFlags.isFetchingInboxes &&
    !props.isFetchingInboxes
  );
});

const isAnyDropdownActive = computed(() => {
  return (
    showContactsDropdown.value ||
    showInboxesDropdown.value ||
    showCcEmailsDropdown.value ||
    showBccEmailsDropdown.value
  );
});

const handleContactSearch = value => {
  showContactsDropdown.value = true;
  emit('searchContacts', {
    keys: ['email', 'phone_number', 'whatsapp_id', 'name'],
    query: value,
  });
};

const handleDropdownUpdate = (type, value) => {
  if (type === 'cc') {
    showCcEmailsDropdown.value = value;
  } else if (type === 'bcc') {
    showBccEmailsDropdown.value = value;
  } else {
    showContactsDropdown.value = value;
  }
};

const searchCcEmails = value => {
  showCcEmailsDropdown.value = true;
  emit('searchContacts', { keys: ['email'], query: value });
};

const searchBccEmails = value => {
  showBccEmailsDropdown.value = true;
  emit('searchContacts', { keys: ['email'], query: value });
};

const setSelectedContact = async ({ value, action, ...rest }) => {
  v$.value.$reset();
  emit('updateSelectedContact', { value, action, ...rest });
  showContactsDropdown.value = false;
  showInboxesDropdown.value = true;
};

const handleInboxAction = ({ value, action, ...rest }) => {
  v$.value.$reset();
  emit('updateTargetInbox', { ...rest });
  showInboxesDropdown.value = false;
  state.attachedFiles = [];
};

const removeTargetInbox = value => {
  v$.value.$reset();
  emit('updateTargetInbox', value);
  state.attachedFiles = [];
};

const clearSelectedContact = () => {
  emit('clearSelectedContact');
  state.attachedFiles = [];
};

const onClickInsertEmoji = emoji => {
  state.message += emoji;
};

const handleAddSignature = signature => {
  state.message = appendSignature(state.message, signature);
};

const handleRemoveSignature = signature => {
  state.message = removeSignature(state.message, signature);
};

const handleAttachFile = files => {
  state.attachedFiles = files;
};

const clearForm = () => {
  Object.assign(state, {
    message: '',
    subject: '',
    ccEmails: '',
    bccEmails: '',
    attachedFiles: [],
  });
  v$.value.$reset();
};

const handleSendMessage = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  try {
    const success = await emit('createConversation', {
      payload: newMessagePayload(),
      isFromWhatsApp: false,
    });
    if (success) {
      clearForm();
    }
  } catch (error) {
    // Form will not be cleared if conversation creation fails
  }
};

watch(
  () => props.targetInbox?.id,
  async inboxId => {
    if (
      !(
        inboxTypes.value.isAWhatsAppChannel ||
        inboxTypes.value.isWhatsAppGupshupInbox ||
        inboxTypes.value.isTwilioWhatsApp
      )
    ) {
      return;
    }
    if (inboxId) {
      whatsappMessageTemplates.value =
        await store.getters['inboxes/getWhatsAppTemplates'](inboxId);
    } else {
      whatsappMessageTemplates.value = [];
    }
  },
  { immediate: true }
);

const handleSendWhatsappMessage = async ({ message, templateParams }) => {
  const whatsappMessagePayload = prepareWhatsAppMessagePayload({
    targetInbox: props.targetInbox,
    selectedContact: props.selectedContact,
    message,
    templateParams,
    currentUser: props.currentUser,
  });
  await emit('createConversation', {
    payload: whatsappMessagePayload,
    isFromWhatsApp: true,
  });
};

const replyWindowBannerMessage = computed(() => {
  if (
    inboxTypes.value.isAWhatsAppChannel ||
    inboxTypes.value.isWhatsAppGupshupInbox
  ) {
    return t('CONVERSATION.TWILIO_WHATSAPP_CAN_REPLY');
  }
  if (inboxTypes.value.isMercadoLibreInbox) {
    return t('INBOX_MGMT.ADD.MERCADOLIBRE.CONVERSATION_CLOSED');
  }
  if (inboxTypes.value.isApi) {
    const { additional_attributes: additionalAttributes = {} } =
      props.targetInbox;
    if (additionalAttributes) {
      const { agent_reply_time_window_message: agentReplyTimeWindowMessage } =
        additionalAttributes;
      return agentReplyTimeWindowMessage;
    }
    return '';
  }
  return t('CONVERSATION.CANNOT_REPLY');
});

const replyWindowLink = computed(() => {
  if (inboxTypes.value.isAWhatsAppChannel) {
    return REPLY_POLICY.FACEBOOK;
  }
  if (inboxTypes.value.isWhatsAppGupshupInbox) {
    return REPLY_POLICY.GUPSHUP;
  }
  if (!inboxTypes.value.isApi) {
    return REPLY_POLICY.TWILIO_WHATSAPP;
  }
  return '';
});

const replyWindowLinkText = computed(() => {
  // Hides the old inline restriction text because the banner now uses a button.
  return '';
});

const shouldShowReplyWindowMoreInfo = computed(() => {
  // Keeps the CTA on the same inbox flags used by the original reply window logic.
  return (
    inboxTypes.value.isApi ||
    inboxTypes.value.isAWhatsAppChannel ||
    inboxTypes.value.isWhatsAppGupshupInbox
  );
});

const replyWindowMoreInfoLabel = computed(() => {
  if (!shouldShowReplyWindowMoreInfo.value) {
    return '';
  }
  // Uses a conversation-specific translation so the CTA stays localized in context.
  return t('CONVERSATION.REPLY_WINDOW_MORE_INFO');
});

const replyWindowMoreInfoTitle = computed(() => {
  if (!shouldShowReplyWindowMoreInfo.value) {
    return '';
  }
  // Uses a dedicated translation so the modal title matches the approved copy.
  return t('CONVERSATION.REPLY_WINDOW_MODAL_TITLE');
});

const replyWindowMoreInfoText = computed(() => {
  if (!shouldShowReplyWindowMoreInfo.value) {
    return '';
  }
  // Uses a dedicated translation so the modal body matches the approved copy.
  return t('CONVERSATION.REPLY_WINDOW_MODAL_TEXT');
});

const openReplyWindowInfoModal = () => {
  // Opens the modal with the expanded explanation for WhatsApp reply restrictions.
  showReplyWindowInfoModal.value = true;
};

const closeReplyWindowInfoModal = () => {
  showReplyWindowInfoModal.value = false;
};

const replyDisabled = computed(() => {
  if (selectedConversation.value) {
    return props.targetInbox && !selectedConversation.value.can_reply;
  }
  return (
    inboxTypes.value.isAWhatsAppChannel ||
    inboxTypes.value.isWhatsAppGupshupInbox
  );
});

defineExpose({});
</script>

<template>
  <Teleport to="body">
    <div
      :style="dropdownPosition"
      class="fixed w-[670px] mt-2 divide-y divide-n-strong overflow-visible transition-all duration-300 ease-in-out justify-between flex flex-col bg-n-alpha-3 border border-n-strong shadow-sm backdrop-blur-[100px] rounded-xl z-[50]"
    >
    <ContactSelector
      :contacts="contacts"
      :selected-contact="selectedContact"
      :show-contacts-dropdown="showContactsDropdown"
      :is-loading="isLoading"
      :is-creating-contact="isCreatingContact"
      :contact-id="contactId"
      :contactable-inboxes-list="contactableInboxesList"
      :show-inboxes-dropdown="showInboxesDropdown"
      :has-errors="validationStates.isContactInvalid"
      @search-contacts="handleContactSearch"
      @set-selected-contact="setSelectedContact"
      @clear-selected-contact="clearSelectedContact"
      @update-dropdown="handleDropdownUpdate"
    />
    <InboxEmptyState v-if="showNoInboxAlert" />
    <InboxSelector
      v-else
      :target-inbox="targetInbox"
      :selected-contact="selectedContact"
      :show-inboxes-dropdown="showInboxesDropdown"
      :contactable-inboxes-list="contactableInboxesList"
      :has-errors="validationStates.isInboxInvalid"
      @update-inbox="removeTargetInbox"
      @toggle-dropdown="showInboxesDropdown = $event"
      @handle-inbox-action="handleInboxAction"
    />

    <EmailOptions
      v-if="inboxTypes.isEmail"
      v-model:cc-emails="state.ccEmails"
      v-model:bcc-emails="state.bccEmails"
      v-model:subject="state.subject"
      :contacts="contacts"
      :show-cc-emails-dropdown="showCcEmailsDropdown"
      :show-bcc-emails-dropdown="showBccEmailsDropdown"
      :is-loading="isLoading"
      :has-errors="validationStates.isSubjectInvalid"
      @search-cc-emails="searchCcEmails"
      @search-bcc-emails="searchBccEmails"
      @update-dropdown="handleDropdownUpdate"
    />
    <Banner
      v-if="replyDisabled"
      action-button-variant="clear"
      action-button-icon=""
      color-scheme="alert"
      :banner-message="replyWindowBannerMessage"
      :href-link="replyWindowLink"
      :href-link-text="replyWindowLinkText"
      :has-action-button="!!replyWindowMoreInfoLabel"
      :action-button-label="replyWindowMoreInfoLabel"
      @primary-action="openReplyWindowInfoModal"
    />
    <!-- Keeps the user in context while showing the WhatsApp policy explanation. -->
    <woot-modal
      v-model:show="showReplyWindowInfoModal"
      size="medium"
      :on-close="closeReplyWindowInfoModal"
    >
      <div class="flex flex-col h-auto overflow-auto">
        <woot-modal-header :header-title="replyWindowMoreInfoTitle" />
        <div class="reply-window-info-modal__content">
          {{ replyWindowMoreInfoText }}
        </div>
      </div>
    </woot-modal>
    <MessageEditor
      v-if="!replyDisabled && !showNoInboxAlert"
      v-model="state.message"
      :is-email-or-web-widget-inbox="inboxTypes.isEmailOrWebWidget"
      :has-errors="validationStates.isMessageInvalid"
      :has-attachments="state.attachedFiles.length > 0"
    />

    <AttachmentPreviews
      v-if="state.attachedFiles.length > 0"
      :attachments="state.attachedFiles"
      @update:attachments="state.attachedFiles = $event"
    />

    <ActionButtons
      :attached-files="state.attachedFiles"
      :target-inbox="targetInbox"
      :is-whatsapp-inbox="
        inboxTypes.isAWhatsAppChannel ||
        inboxTypes.isWhatsAppGupshupInbox ||
        inboxTypes.isTwilioWhatsApp
      "
      :is-reply-enabled="!replyDisabled"
      :is-email-or-web-widget-inbox="inboxTypes.isEmailOrWebWidget"
      :is-twilio-sms-inbox="inboxTypes.isTwilioSMS"
      :message-templates="whatsappMessageTemplates"
      :channel-type="inboxChannelType"
      :is-loading="isCreating"
      :disable-send-button="isCreating"
      :has-no-inbox="showNoInboxAlert"
      :is-dropdown-active="isAnyDropdownActive"
      @insert-emoji="onClickInsertEmoji"
      @add-signature="handleAddSignature"
      @remove-signature="handleRemoveSignature"
      @attach-file="handleAttachFile"
      @discard="$emit('discard')"
      @send-message="handleSendMessage"
      @send-whatsapp-message="handleSendWhatsappMessage"
    />
  </div>
    </Teleport>

</template>

<style scoped lang="scss">
.reply-window-info-modal__content {
  @apply px-8 pt-4 pb-8 text-sm leading-6 text-slate-600 dark:text-slate-300 whitespace-pre-line;
}
</style>
